import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Input,
  Textarea,
  Button,
  Spinner,
} from '@material-tailwind/react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';

function CoverSection({ content, onSave, onReset, isSaving }) {
  const [editedTitle, setEditedTitle] = useState('');
  const [editedSubtitle, setEditedSubtitle] = useState('');
  const [editedCustomerName, setEditedCustomerName] = useState('');
  const [editedAircraftDescription, setEditedAircraftDescription] = useState('');
  const [editedDate, setEditedDate] = useState('');
  const [editedPreparedBy, setEditedPreparedBy] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form from content
  useEffect(() => {
    if (content) {
      setEditedTitle(content.title || '');
      setEditedSubtitle(content.subtitle || '');
      setEditedCustomerName(content.customerName || content.customer_name || '');
      setEditedAircraftDescription(content.aircraftDescription || content.aircraft_description || '');
      setEditedDate(content.date || '');
      setEditedPreparedBy(content.preparedBy || content.prepared_by || '');
      setHasChanges(false);
    }
  }, [content]);

  // Check for changes
  useEffect(() => {
    if (!content) return;
    const changed =
      editedTitle !== (content.title || '') ||
      editedSubtitle !== (content.subtitle || '') ||
      editedCustomerName !== (content.customerName || content.customer_name || '') ||
      editedAircraftDescription !== (content.aircraftDescription || content.aircraft_description || '') ||
      editedDate !== (content.date || '') ||
      editedPreparedBy !== (content.preparedBy || content.prepared_by || '');
    setHasChanges(changed);
  }, [editedTitle, editedSubtitle, editedCustomerName, editedAircraftDescription, editedDate, editedPreparedBy, content]);

  const handleSave = () => {
    if (onSave) {
      onSave({
        title: editedTitle,
        subtitle: editedSubtitle,
        customerName: editedCustomerName,
        aircraftDescription: editedAircraftDescription,
        date: editedDate,
        preparedBy: editedPreparedBy,
      });
    }
  };

  const handleReset = () => {
    if (content) {
      setEditedTitle(content.title || '');
      setEditedSubtitle(content.subtitle || '');
      setEditedCustomerName(content.customerName || content.customer_name || '');
      setEditedAircraftDescription(content.aircraftDescription || content.aircraft_description || '');
      setEditedDate(content.date || '');
      setEditedPreparedBy(content.preparedBy || content.prepared_by || '');
      setHasChanges(false);
    }
    if (onReset) {
      onReset();
    }
  };

  if (!content) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <Typography>No cover page data available</Typography>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-2 overflow-auto h-full">
      <Card className="border border-blue-200">
        <CardBody className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-blue-500" />
              <Typography variant="small" className="font-medium text-blue-800">
                Cover Page Details
              </Typography>
            </div>
            {hasChanges && (
              <span className="text-xs text-amber-600 font-medium">Modified</span>
            )}
          </div>

          <div className="space-y-4">
            <Input
              label="Report Title"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              size="md"
            />

            <Input
              label="Subtitle"
              value={editedSubtitle}
              onChange={(e) => setEditedSubtitle(e.target.value)}
              size="md"
            />

            <Input
              label="Customer Name"
              value={editedCustomerName}
              onChange={(e) => setEditedCustomerName(e.target.value)}
              size="md"
            />

            <Textarea
              label="Aircraft Description"
              value={editedAircraftDescription}
              onChange={(e) => setEditedAircraftDescription(e.target.value)}
              rows={2}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Date"
                value={editedDate}
                onChange={(e) => setEditedDate(e.target.value)}
                size="md"
              />

              <Input
                label="Prepared By"
                value={editedPreparedBy}
                onChange={(e) => setEditedPreparedBy(e.target.value)}
                size="md"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outlined"
              size="sm"
              onClick={handleReset}
              disabled={!hasChanges || isSaving}
              className="py-1 px-3 text-xs"
            >
              Reset
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="py-1 px-3 text-xs"
            >
              {isSaving ? (
                <>
                  <Spinner className="h-3 w-3 mr-1" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

export default CoverSection;
